import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import type { Lead, UserProfile } from "../../../../client";
import {
  useCreateLead,
  useDeleteLead,
  useGetClient,
  useUpdateLead,
} from "../../../../services";
import { LeadRow } from "./LeadRow";

interface Props {
  organizationId: string;
  profiles?: UserProfile[];
  lead: Lead;
  index?: number;
  profileId?: string;
  childLeads?: Lead[];
  hideProfileList?: boolean;
}

export const ExistingLeadRow = ({
  organizationId,
  profiles,
  lead,
  index,
  profileId,
  childLeads,
  hideProfileList,
}: Props) => {
  if (!lead.id) throw new Error("Missing lead ID");

  const toastId = useRef<string | number>();

  const deleteLead = useDeleteLead(organizationId, {
    onMutate: (payload) => {
      console.log("onMutate triggered with payload:", payload);
    },

    onError: () => {
      toast.error("Something went wrong", { id: toastId.current });
    },
  });

  const updateLead = useUpdateLead(
    {
      onMutate: (payload) => {
        if (payload.body.status === "archived") {
          toastId.current = toast.loading("Archiving lead...");
        }
      },
      onSuccess: (_, { body: { status } }) => {
        if (status === "archived")
          toast.success("Lead archived", { id: toastId.current });
      },
      onError: () => {
        toast.error("Something went wrong", { id: toastId.current });
      },
    },
    profileId,
  );

  const createChildLead = useCreateLead({});

  const onAssignProfile = useCallback(
    (selectedProfileId: string) => {
      createChildLead.mutate({
        body: {
          ...lead,
          id: "",
          parentLeadId: lead.id,
          profileId: selectedProfileId,
          profileLinkId: "",
          contractId: "",
          orgId: organizationId,
        },
        path: {
          organizationID: organizationId,
        },
      });
    },
    [createChildLead, lead, organizationId],
  );

  const { data: client } = useGetClient({
    clientId: updateLead.variables?.body.clientId ?? lead.clientId ?? "",
    organizationID: organizationId,
  });

  const onUpdateLead = useCallback(
    (payload: Partial<Lead>) => {
      updateLead.mutate({
        path: { organizationID: organizationId, leadID: lead.id ?? "" },
        body: { ...lead, ...payload },
      });
    },
    [updateLead, organizationId, lead],
  );

  return (
    <LeadRow
      key={index}
      lead={updateLead.variables?.body ?? lead}
      organizationId={organizationId}
      profiles={profiles}
      client={client}
      onUpdateLead={(payload: Partial<Lead>) => {
        console.log("updating lead with profileId " + profileId);
        onUpdateLead(payload);
      }}
      onDeleteLead={(leadId: string) =>
        deleteLead.mutate({
          path: { organizationID: organizationId, leadID: leadId },
        })
      }
      profileId={profileId}
      childLeads={childLeads ?? []}
      onAssignProfile={childLeads !== undefined ? onAssignProfile : undefined}
      hideProfileList={hideProfileList}
      isCreatingChildLead={createChildLead.isPending}
    />
  );
};

ExistingLeadRow.displayName = "ExistingLeadRow";
